import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import { PaginationItem } from '@material-ui/lab'
import SearchIcon from '@material-ui/icons/Search'
import { useTranslation } from '@flint/locales'
import { Layout } from 'shared'
import { GET_QA_ORDERS } from 'global/graphql/SimpleProjectQA'

const ITEMS_PER_PAGE = 10

const useStyles = makeStyles(() => ({
  paginationWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    backgroundColor: 'rgb(241, 243, 242)',
    borderRadius: 8,
    marginTop: 8,
  },
  paginationUl: {
    '& .MuiPagination-ul': {
      flexWrap: 'nowrap',
    },
  },
  activeItem: {
    '&.Mui-selected': {
      backgroundColor: 'black !important',
      color: 'white',
    },
  },
}))

export const QAOrdersList = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const classes = useStyles()

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, loading, error } = useQuery(GET_QA_ORDERS, {
    variables: { projectId: parseInt(projectId, 10) },
  })

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <Box p={2}>
          <Typography color="error">{error.message}</Typography>
        </Box>
      </Layout>
    )
  }

  const orders: any[] = data?.orders?.data ?? []

  const filtered = orders.filter((o) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return (
      o.title?.toLowerCase().includes(q) || o.code?.toLowerCase().includes(q)
    )
  })

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  return (
    <Layout>
      <Box p={2} maxWidth={700} mx="auto">
        <Typography variant="h6" gutterBottom>
          قائمة الطلبات
        </Typography>
        <Divider />

        <Box mt={2} mb={1}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={t('search')}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List disablePadding>
          {paginated.map((order) => (
            <React.Fragment key={order.id}>
              <ListItem
                button
                onClick={() => navigate(`${order.code}`, { state: { order } })}
                style={{ paddingTop: 12, paddingBottom: 12 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {order.title || order.code}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      {order.code}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}

          {paginated.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography color="textSecondary">
                    {search ? t('no search result') : t('no_tasks')}
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>

        {totalPages > 1 && (
          <div className={classes.paginationWrapper}>
            <Pagination
              className={classes.paginationUl}
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={item.selected ? classes.activeItem : undefined}
                />
              )}
            />
          </div>
        )}
      </Box>
    </Layout>
  )
}
